import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { Observable, of } from 'rxjs';
import { map, switchMap, take } from 'rxjs/operators';
import { PlanFeatures } from 'src/app/shared/models/plan.model';
import { PlanService } from 'src/app/shared/services/plan.service';
import { ShopState } from 'src/app/shared/ngrxState/shop.reducer';
import { RoleService } from 'src/app/core/services/role.service';

@Injectable({
  providedIn: 'root',
})
export class PlanGuard implements CanActivate {
  constructor(
    private router: Router,
    private store: Store<{ shop: ShopState }>,
    private roleService: RoleService,
    private planService: PlanService,
  ) {}

  canActivate(route: ActivatedRouteSnapshot): Observable<boolean> {
    const requiredFeature = route.data['planFeature'] as
      | keyof PlanFeatures
      | undefined;
   
    const allowFreePlan = !!route.data['allowFreePlan'];

    return this.roleService.getSignedInProfile().pipe(
      take(1),
      switchMap((user) => {
        if (!user?.uid) {
          void this.router.navigate(['/auth/login']);
          return of(false);
        }
        if (user.role === 'SuperAdmin') {
          return of(true);
        }

        const userId = user.userParentId || user.uid;
        return this.planService.getAccountPlan(userId).pipe(
          map((account) => {
            if (allowFreePlan) {
              return true;
            }
            const plan = account?.plan;
            if (!plan) return false;
            if (requiredFeature) {
              return this.planService.hasFeature(plan, requiredFeature);
            }
            return account.plan_id !== 'free';
          }),
        );
      }),
      switchMap((allowed) => {
        if (allowed) return of(true);

        return this.store.select((state) => state.shop.selectedShop).pipe(
          take(1),
          map((shop) => {
            const shopId = shop?.id;
            if (shopId) {
              void this.router.navigate([
                '/plans/shop',
                shopId,
                'voice-shoppping',
              ]);
            } else {
              void this.router.navigate(['/plans']);
            }
            return false;
          }),
        );
      }),
    );
  }
}
